// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// patient-web API client. Mirrors provider-admin's api.ts, but authenticates with the
// patient session JWT (from lib/session) instead of a Firebase ID token. All PHI is
// fetched here and rendered ephemerally — never persisted client-side.

import { getSessionToken } from './session'

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** True when the failure was an auth rejection — callers route back to /verify. */
export function isAuthError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403)
}

/** Low-level fetch: resolves URL, attaches the session bearer + a request id. */
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const url = /^https?:\/\//.test(input) ? input : `${API_BASE}${input}`

  const headers = new Headers(init.headers)
  headers.set('X-Request-ID', crypto.randomUUID())

  const token = getSessionToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  return fetch(url, { ...init, headers })
}

/** Typed JSON request. Throws ApiError on non-2xx; returns undefined for 204. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await apiFetch(path, { ...init, headers })

  if (!res.ok) {
    let code = 'unknown'
    try {
      const body = (await res.json()) as { error?: string; code?: string }
      code = body.error ?? body.code ?? code
    } catch {
      // body not JSON — keep 'unknown'
    }
    throw new ApiError(res.status, code, `API ${res.status}: ${code}`)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Convenience POST with a JSON body. */
export function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) })
}

// ── Patient identity bind ──────────────────────────────────────────────────────
// Binds the phone+Google Firebase identity (qh-patients tenant) to the deeplink-known
// patient record. Authenticated by the current session JWT (via apiFetch); the backend
// verifies the Firebase ID token, links patient_user → patient, stamps the session, and
// returns a refreshed session JWT that now carries patient_user_id.

export interface BindIdentityResponse {
  sessionJwt: string
}

export function bindIdentity(firebaseIdToken: string): Promise<BindIdentityResponse> {
  return postJson<BindIdentityResponse>('/v1/patient/identity/bind', { firebaseIdToken })
}

// ── Patient phone enrollment (on-file OTP) ─────────────────────────────────────
// Enrolls a phone login credential AFTER identity bind (the session must be bound, else the
// backend 409s identity_not_bound). The client never sends a raw phone number: the server
// resolves the provider-vetted on-file number and returns only masked hints.

export interface PhoneHintResponse {
  /** Masked on-file number(s) the OTP would be sent to, e.g. "•••-•••-0123". Never raw. */
  phoneHints: string[]
}

export interface PhoneStartResponse {
  /** Masked number the OTP was sent to (client never learns the raw value). */
  sentTo: string
}

export interface PhoneVerifyResponse {
  enrolled: boolean
  /** Masked hint for the now-enrolled credential, e.g. "•••-•••-0123". */
  displayHint: string
}

export function getPhoneHint(): Promise<PhoneHintResponse> {
  return request<PhoneHintResponse>('/v1/patient/phone/hint')
}

export function startPhone(): Promise<PhoneStartResponse> {
  return postJson<PhoneStartResponse>('/v1/patient/phone/start', {})
}

export function verifyPhone(code: string): Promise<PhoneVerifyResponse> {
  return postJson<PhoneVerifyResponse>('/v1/patient/phone/verify', { code })
}
